import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { apiGet, apiPut } from '../generic/Api_Functions'
import { IconButton, Stack } from '@mui/material'
import { Edit, TextSnippetOutlined, Visibility } from '@mui/icons-material'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import IncidenceForm from './IncidenceForm'
import useUser from '../generic/hooks/useUser/useUser'
import Document from '../configuration/document/Document'
import ButtonBack from '../generic/buttons/floatingButtons/ButtonBack'
import { useNavigate } from 'react-router'

const Incidence = () => {
  // Data from navigation
  const location = useLocation()
  /** @type {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} */
  const deliveryNote = location?.state?.deliveryNote
  /** @type {'DELIVERY_NOTE'|'THEORETICAL'|'QUALITY/QUANTITY'} **/
  const type = location?.state?.type
  const currentSelectedId = location?.state?.currentSelectedId
  const backWindow = location?.state?.window || 'ORDER_SELECTOR'
  const navigate = useNavigate()

  // User data
  const { t_ } = useSmartTranslation()
  const [openIncidenceDialog, setOpenIncidenceDialog] = useState(false)
  /** @type {[import('../generic/utilities/objectStructures/materialreception/materialReception').IncidenceForm, React.Dispatch<SetStateAction<import('../generic/utilities/objectStructures/materialreception/materialReception').IncidenceForm>>]} */
  const [incidence, setIncidence] = useState(null)
  const { user } = useUser()
  const [rowIncidence, setRowIncidence] = useState(null)
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)

  const loadIncidenceData = async (row) => {
    if (row) {
      setIncidence(row)
      setOpenIncidenceDialog(true)
      return
    }
    if (!deliveryNote) {
      return
    }
    /** @type {import('../generic/utilities/objectStructures/materialreception/materialReception').IncidenceForm} **/
    const data = { user: { id: user.id, name: user.username }, deliveryNote: { id: deliveryNote.id, code: deliveryNote.recordCode, number: deliveryNote.number, date: deliveryNote.date }, solved: 'NO' }
    if (type === 'THEORETICAL') {
      const theoretical = deliveryNote.purchaseOrderTheoreticalReceptions.find(purchaseOrderTheoreticalReception => purchaseOrderTheoreticalReception.id === currentSelectedId)
      if (theoretical) {
        const purchaseOrder = await apiGet('purchaseOrder/' + theoretical.purchaseOrder.id)
        data.purchaseOrder = purchaseOrder.purchaseOrderCode
        data.purchaseOrderPosition = purchaseOrder.lineOrderPosition
        data.rawMaterialReferenceCode = purchaseOrder.rawMaterialReference?.code
        data.rawMaterialReferenceDescription = purchaseOrder.rawMaterialDescription
        data.entryQuantity = theoretical.receivedTheoreticalQuantity
        data.project = purchaseOrder.projectCode
        data.supplierCode = purchaseOrder.supplier?.code
        data.supplierName = purchaseOrder.supplier?.name
        data.purchaseOrderTheoreticalReception = theoretical.id
      }
    } else if (type === 'QUALITY/QUANTITY') {
      for (const purchaseOrderTheoreticalReception of deliveryNote.purchaseOrderTheoreticalReceptions) {
        let cont = true
        for (const purchaseOrderReception of purchaseOrderTheoreticalReception.purchaseOrderReceptions) {
          if (purchaseOrderReception.id === currentSelectedId) {
            const purchaseOrder = await apiGet('purchaseOrder/' + purchaseOrderTheoreticalReception.purchaseOrder.id)
            data.purchaseOrder = purchaseOrder.purchaseOrderCode
            data.purchaseOrderPosition = purchaseOrder.lineOrderPosition
            data.rawMaterialReferenceCode = purchaseOrder.rawMaterialReference?.code
            data.rawMaterialReferenceDescription = purchaseOrder.rawMaterialDescription
            data.project = purchaseOrder.projectCode
            data.supplierCode = purchaseOrder.supplier?.code
            data.supplierName = purchaseOrder.supplier?.name
            data.receivedQuantity = purchaseOrderReception.receivedQuantity
            data.entryQuantity = purchaseOrderReception.receivedTheoreticalQuantity
            data.serialNumber = purchaseOrderReception.batchNumber
            data.purchaseOrderReception = purchaseOrderReception.id
            data.recordPos = purchaseOrderReception.recordPos
            cont = false
            break
          }
        }
        if (!cont) {
          break
        }
      }
    }
    setIncidence(data)
    setOpenIncidenceDialog(true)
  }

  const setOpen = () => {
    if (!backWindow) {
      navigate(-1)
      return
    }

    const routeState = { window: backWindow, deliveryNote }

    navigate('/mos/reception', { state: routeState })
  }

  const columns = [
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Record code'),
      field: 'deliveryNote.code'
    },
    {
      name: t_('Record position'),
      field: 'recordPos'
    },
    {
      name: t_('Solved'),
      field: 'solved',
      render: (row) => (row.solved ? t_(row.solved) : '')
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Type'),
      field: 'incidence.code'
    },
    {
      name: t_('Class'),
      field: 'classIncidence',
      // The form shows the class through t_ (IncidenceForm.jsx), the table reads the same.
      render: (row) => (row.classIncidence ? t_(row.classIncidence) : '')
    },
    {
      name: t_('Order'),
      field: 'purchaseOrder'
    },
    {
      name: t_('Position'),
      field: 'purchaseOrderPosition'
    },
    {
      name: t_('Delivery note'),
      field: 'deliveryNote.number'
    },
    {
      name: t_('Raw material reference'),
      field: 'rawMaterialReferenceCode'
    },
    {
      name: t_('Description'),
      field: 'rawMaterialReferenceDescription',
      readOnly: true
    }
  ]

  const actions = {
    create: { disabled: !deliveryNote },
    custom: [
      {
        name: row => (row.closeDate ? 'View' : 'Edit'),
        render: (row) => {
          const isClosed = row.closeDate !== null && row.closeDate !== undefined
          return (
            <IconButton>
              {isClosed ? <Visibility /> : <Edit />}
            </IconButton>
          )
        },
        onClick: (row) => {
          loadIncidenceData(row)
        }
      },
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          setRowIncidence({ id: row.id, code: row.code, name: row.code })
          setShowDocument(true)
        }
      }
    ]
  }

  // Maps the origin view (type) to the backend scope query params. Absence of
  // scope (DELIVERY_NOTE or no type at all) keeps the retro-compatible GENERAL
  // behaviour: no scope params are sent.
  const buildScopeQuery = () => {
    const scopeTypeByType = {
      THEORETICAL: 'THEORETICAL',
      'QUALITY/QUANTITY': 'QUANTITATIVE'
    }
    const scopeType = scopeTypeByType[type]
    if (!scopeType) {
      return ''
    }
    const params = new URLSearchParams({ scopeType, scopeId: currentSelectedId })
    return '?' + params.toString()
  }

  const dataFetch = async (pagination) => {
    if (deliveryNote) {
      return await apiPut('materialReceptionIncidence/getByDeliveryNote/' + deliveryNote.id + buildScopeQuery(), pagination)
    }
    return await apiPut('materialReceptionIncidence/getAllIncidences/', pagination)
  }

  return (
    <Stack spacing={2}>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={rowIncidence} resourceType='MATERIAL_RECEPTION_INCIDENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartDialog
        setOpen={openIncidenceDialog}
        renderComponent={<IncidenceForm type={type} incidence={incidence} setIncidence={setIncidence} />}
        accept={false}
        cancel={false}
        close
        closeCallback={() => setOpenIncidenceDialog(false)}
        addButtonDisabled
      />

      <SmartMateriaUITable
        title={t_('Incidence')}
        tableTuneUniqueKey='MaterialReceptionIncidence'
        columns={columns}
        dataFetch={dataFetch}
        pageable
        actions={actions}
        onAddElementClick={() => { loadIncidenceData() }}
        customAddRecordClick
      />
      <Stack direction='row' justifyContent='flex-end' width='100%'>
        <ButtonBack
          buttonAction={() => setOpen()}
        />
      </Stack>
    </Stack>
  )
}

export default Incidence
